import { type Maybe, maybe, rd, type RemoteData } from '@passionware/monads';
import type { Resource } from '__types__/v1/models';
import { MapPin, Tag, Users } from 'lucide-react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { ResourceSearchQuery } from 'v5/api/resource/resource.api';
import type { ResourceKinds } from 'v5/core/entity-values/values.types';
import {
  type KindOf,
  readField,
  type SpecOf,
  type UnitValues
} from 'v5/core/form-schema/formKinds.types';
import { useResourceSchemaCopy } from 'v5/features/resources/form/useResourceSchemaCopy';
import {
  buildShortlistDisplayFields,
  formatShortlistRate,
  type ShortlistDisplayField
} from 'v5/features/shortlists/display/shortlistDisplayFields.logic';
import { shortlistDisplayKit } from 'v5/features/shortlists/display/shortlistDisplayKit';
import type { ResultDescription } from 'v5/patterns/resource-picker/ResourcePickerResults';
import { resourceRow } from 'v5/patterns/resource-picker/resourceRow';
import { formatRateSummary } from 'v1/helpers/budgetHelper';
import {
  DEFAULT_RESOURCE_PICKER_RESULT_FIELDS,
  type ResourcePickerResultFields,
  type UserPreferencesService
} from 'v5/services/front/UserPreferencesService/UserPreferencesService';
import {
  BADGE_FIELDS,
  type BadgeField,
  firstAndMore,
  isBadgeField,
  resultLine
} from './resultFields.logic';
import {
  resourceFieldLabel,
  type ResourceFieldGroup
} from './resourceFieldCatalog.logic';
import {
  useResourceFieldCatalog,
  type ResourceFieldCatalogServices
} from './useResourceFieldCatalog';

export type ResultFieldsServices = ResourceFieldCatalogServices & {
  /** Without it, the default fields, not kept. */
  userPreferencesService?: UserPreferencesService;
};

export interface ResultFields {
  value: ResourcePickerResultFields;
  onChange: (value: ResourcePickerResultFields) => void;
  /** The fields a result can show: the role, then the card fields' catalogue. */
  catalog: RemoteData<ResourceFieldGroup[]>;
  labelOf: (key: string) => string;
  /** For `ResourcePicker.Results`: the line and the badges of the user's fields. */
  describe: (resource: Resource) => ResultDescription;
  /**
   * The host's search as `ResourcePicker.Count` and `.Results` read it: asking for each result's
   * first pins while the user shows pictures on the right.
   */
  queryOf: <Q extends ResourceSearchQuery>(query: Maybe<Q>) => Maybe<Q>;
}

const BADGE_ICONS = { locations: MapPin, tags: Tag, groups: Users };

/**
 * The user's own result fields, the same in every picker that adds resources: what each result
 * says under its name and which list it shows as badges.
 */
export function useResultFields(services: ResultFieldsServices): ResultFields {
  const { t } = useTranslation('patterns', { keyPrefix: 'ResourcePicker' });
  const { t: tFields } = useTranslation('resource-fields', {
    keyPrefix: 'ResultFields'
  });
  const store = services.userPreferencesService?.resourcePickerResultFields;
  const value =
    rd.tryGet(store?.useValue() ?? rd.ofIdle()) ??
    DEFAULT_RESOURCE_PICKER_RESULT_FIELDS;
  const scopeRd = services.appScopeService.useResolvedAppScope();
  const workspaceId =
    rd.tryMap(scopeRd, scope =>
      scope.level === 'organization' ? null : scope.workspaceId
    ) ?? null;
  const catalog = rd.map(useResourceFieldCatalog(services), groups =>
    addRole(groups, tFields('role'))
  );
  const definitions =
    rd.tryGet(
      services.metastructureMappingService.useCustomFieldDefinitions()
    ) ?? {};
  const copy = useResourceSchemaCopy();

  const labelOf = (key: string) =>
    key === 'role'
      ? tFields('role')
      : resourceFieldLabel(key, rd.tryGet(catalog) ?? [], definitions);

  const describe = useCallback(
    (resource: Resource): ResultDescription => {
      const fieldKeys = value.line.filter(
        key => key !== 'role' && key !== 'rates' && !isBadgeField(key)
      );
      const fields = new Map(
        buildShortlistDisplayFields({
          defaultFields: fieldKeys,
          resource,
          definitions: Object.values(definitions),
          workspaceId,
          labelOf: (_key, fallback) => fallback,
          copy,
          typeFields: { type: 'unknown' },
          qr: { type: 'off' }
        }).map(field => [field.key, field])
      );
      const textOf = (key: string): string | null => {
        if (key === 'role') return resource.role?.trim() || null;
        if (key === 'rates') return rateText(resource);
        if (isBadgeField(key)) return firstAndMore(badgeValues(resource, key));
        const field = fields.get(key);
        return field ? displayFieldText(field) : null;
      };
      return {
        line: resultLine(value.line, textOf, key => {
          switch (key) {
            case 'role':
              return t('noRole');
            case 'rates':
              return t('noRate');
            default:
              return null;
          }
        }),
        badges: BADGE_FIELDS.filter(key => value.right.includes(key)).map(
          key => ({
            key,
            values: badgeValues(resource, key),
            icon: BADGE_ICONS[key]
          })
        )
      };
    },
    [value, definitions, workspaceId, copy, t]
  );

  return {
    value,
    onChange: next => store?.setValue(next),
    catalog,
    labelOf,
    describe,
    queryOf: query =>
      maybe.map(query, search => ({
        ...search,
        includePinsPreview: value.right.includes('pictures')
      }))
  };
}

/** The role first among the profile's fields: a result showed it before any card field. */
function addRole(
  groups: ResourceFieldGroup[],
  label: string
): ResourceFieldGroup[] {
  const role = { key: 'role', label };
  const profile = groups.find(group => group.id === 'profile');
  return profile
    ? groups.map(group =>
        group === profile
          ? { ...group, options: [role, ...group.options] }
          : group
      )
    : [{ id: 'profile', onlyTypes: null, options: [role] }, ...groups];
}

/** As the row always read the rate: its first one with an amount. */
function rateText(resource: Resource): string | null {
  const rate = resourceRow.firstRate(resource);
  return rate
    ? formatRateSummary(
        {
          amount: rate.amount,
          currency: rate.currency,
          interval: rate.interval,
          quantity: 1
        },
        rate.currency || 'USD',
        false
      ) || null
    : null;
}

function badgeValues(resource: Resource, key: BadgeField): string[] {
  switch (key) {
    case 'locations':
      return resourceRow.locations(resource);
    // a tag or group without a name says nothing: left out, in the line too
    case 'tags':
      return (resource.tags ?? [])
        .map(tag => tag.tag?.trim() ?? '')
        .filter(Boolean);
    case 'groups':
      return (resource.groups ?? [])
        .map(group => group.name?.trim() ?? '')
        .filter(Boolean);
  }
}

function displayFieldText(field: ShortlistDisplayField): string | null {
  switch (field.type) {
    case 'kit':
      return kitText(field.spec, { [field.spec.key]: field.value });
    case 'rate':
      return formatShortlistRate(field.money);
    case 'static':
      return field.text || null;
    case 'empty':
    case 'legacy':
    case 'qr':
      return null;
  }
}

function kitText<K extends KindOf<ResourceKinds>>(
  spec: SpecOf<ResourceKinds, K>,
  values: UnitValues<ResourceKinds>
): string | null {
  return (
    shortlistDisplayKit[spec.kind].text?.(
      readField<ResourceKinds, K>(values, spec),
      spec
    ) ?? null
  );
}
